import Image from "next/image";
import Link from "next/link";
import type { EventbriteEventFormatted } from "@/lib/eventbrite";

export function EventbriteEventCard({ event }: { event: EventbriteEventFormatted }) {
  return (
    <div className="bg-white rounded-lg overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
      <div className="relative h-48 w-full overflow-hidden bg-slate-100">
        <Image
          src={event.image}
          alt={event.title}
          fill
          className="object-cover hover:scale-105 transition-transform duration-300"
        />
        {event.isPrivate && (
          <span className="absolute top-3 left-3 rounded bg-slate-900/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
            Invitation only
          </span>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {event.title}
        </h3>

        <div className="space-y-2 text-sm text-slate-600 mb-4 flex-1">
          <div className="flex items-start gap-2">
            <span className="text-[var(--primary)] font-semibold min-w-fit">
              📅
            </span>
            <span>{event.date}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[var(--primary)] font-semibold min-w-fit">
              🕐
            </span>
            <span>{event.time}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[var(--primary)] font-semibold min-w-fit">
              📍
            </span>
            <span>{event.location}</span>
          </div>
          {event.isFree && (
            <div className="flex items-start gap-2">
              <span className="text-[var(--primary)] font-semibold min-w-fit">
                🎟️
              </span>
              <span>Free event</span>
            </div>
          )}
          {event.isPrivate && (
            <div className="flex items-start gap-2">
              <span className="text-[var(--primary)] font-semibold min-w-fit">
                👥
              </span>
              <span>Invitation only</span>
            </div>
          )}
        </div>

        <p className="text-sm text-slate-600 mb-6 line-clamp-3">
          {event.description}
        </p>

        {event.isPrivate ? (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[var(--primary)] text-white px-6 py-2 rounded text-xs font-semibold uppercase tracking-[0.3em] text-center hover:opacity-90 transition"
          >
            View on Eventbrite
          </a>
        ) : event.url ? (
          // Public events now open a native detail page on our site (which in turn
          // hands ticket purchase off to Eventbrite), instead of linking straight
          // out to eventbrite.com. Private events keep the external link above.
          <Link
            href={`/events/${event.id}` as Parameters<typeof Link>[0]["href"]}
            className="inline-block bg-[var(--primary)] text-white px-6 py-2 rounded text-xs font-semibold uppercase tracking-[0.3em] text-center hover:opacity-90 transition"
          >
            View Details
          </Link>
        ) : (
          <Link
            href="/contact"
            className="inline-block bg-[var(--primary)] text-white px-6 py-2 rounded text-xs font-semibold uppercase tracking-[0.3em] text-center hover:opacity-90 transition"
          >
            Learn More
          </Link>
        )}
      </div>
    </div>
  );
}
